<template>
  <div
    class="details-hero"
    :class="[
      {
        'details-hero--square-avatar': squareAvatar,
      },
    ]"
    :style="detailsHeroStyles"
  >
    <div
      class="details-hero__center"
      :style="detailsHeroCenterStyles"
    >
      <div class="details-hero__avatar-container">
        <slot name="avatar">
          <tm-avatar
            class="details-hero__avatar"
            :custom-size="avatarSize"
            :custom-icon="icon"
            :custom-icon-class="iconClass"
            :url="iconUrl"
            :name="title"
            :placeholder-type="avatarPlaceholderType"
            :placeholder-custom-size="avatarPlaceholderSize"
            :color="avatarColor"
            :emoji="emoji"
            :status-offset-px="avatarStatusOffsetPx"
            bordered
          >
            <template v-slot:status>
              <slot name="avatar-status" />
            </template>
          </tm-avatar>
        </slot>
      </div>
      <div
        class="details-hero__title"
        :class="{
          'pr-5': !!$slots.right,
        }"
      >
        <slot name="title">
          <div class="details-hero__title-text truncate">
            <slot name="title-text">{{ title }}</slot>
          </div>
        </slot>
        <div class="details-hero__description">
          <slot>
            <ul
              v-if="infos"
              class="details-hero__list"
            >
              <li
                v-for="info in infos"
                :key="info.label"
                class="d-inline-flex align-center"
                :class="{
                  'flex-shrink-0': info.fitContent,
                }"
              >
                <details-hero-info-item :info="info" />
              </li>
            </ul>
          </slot>
          <slot name="after-default" />
        </div>
      </div>

      <slot
        v-if="!!$slots.right || !!$slots['right-content']"
        name="right-content"
      >
        <details-hero-auto-size-panel
          :root-styles="{
            height: avatarSize,
          }"
          :max-visible-items="maxVisibleItems"
          :dropdown-close-on-click="dropdownCloseOnClick"
        >
          <slot name="right" />
        </details-hero-auto-size-panel>
      </slot>
    </div>

    <div class="details-hero__bottom">
      <slot name="bottom-left" />
    </div>
  </div>
</template>

<script setup lang="ts">
import TmIcon from '@/components/shared/TmIcon.vue'
import TmAvatar from '@/components/shared/TmAvatar.vue'
import TmButton from '@/components/shared/TmButton.vue'
import { detailsHeroProps } from '@/components/shared/templates/details/type'
import DetailsHeroAutoSizePanel from '@/components/shared/templates/details/DetailsHeroAutoSizePanel.vue'

// The ?inline query param is not working for some reason, so we're using a custom plugin to handle this as a workaround:
// @see https://github.com/vitejs/vite/discussions/16295#discussioncomment-10736176
import backgroundImage from '@/assets/images/patterns/pattern-blue.svg?dataUrl'
import backgroundImageSmall from '@/assets/images/patterns/pattern-blue-small.svg?dataUrl'
import { computed } from '@/composition/vue/compositionApi'
import DetailsHeroInfoItem from '@/components/shared/templates/details/DetailsHeroInfoItem.vue'
import type { PropType } from '@/composition/vue/compositionApi'

const backgroundImageUrl = `url("${backgroundImage}")` as const
const backgroundImageUrlSmall = `url("${backgroundImageSmall}")` as const

const props = defineProps({
  ...detailsHeroProps,
  overridePaddingBottom: {
    type: String,
  },
  overrideDetailsHeroCenterAlignItems: {
    type: String as PropType<CSSStyleDeclaration['alignItems']>,
  },
})

const detailsHeroStyles = computed(() => {
  if (!props.overridePaddingBottom) {
    return undefined
  }
  return {
    'padding-bottom': props.overridePaddingBottom,
  }
})

const detailsHeroCenterStyles = computed(() => {
  return {
    'align-items': props.overrideDetailsHeroCenterAlignItems ?? 'center',
  }
})
</script>

<style scoped lang="scss">
.details-hero {
  padding: $detailsHeroPadding;
  color: $emphasize;
  border-radius: $border-radius-md;
  position: relative;
  background: v-bind(backgroundImageUrl) center center / cover no-repeat $blue100;

  @include media-sm-x-max {
    padding: $detailsHeroPaddingMobile;
  }

  @include media-sm-max {
    background-image: v-bind(backgroundImageUrlSmall);
  }

  &__center {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__avatar-container {
    flex-shrink: 0;
    margin-right: 16px;

    @include media-sm-x-max {
      display: none;
    }
  }

  &__bottom {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: flex-end;
  }

  &__list {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    list-style: none;
    line-height: 20px;

    li {
      padding: 0;
      white-space: nowrap;
      min-width: 0; /* Needed for truncation to work */
      + li {
        display: flex;
        align-items: center;
        &:before {
          content: '';
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: $light;
          margin: 0 10px;
        }
      }
    }
  }

  &__title {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
    justify-content: center;
    align-items: flex-start;

    @include media-sm-max {
      max-width: fit-content;
    }

    &-text {
      font-size: $md-x-font;
      line-height: 24px;
      font-weight: $bold-font;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-field {
      width: 100%;
      margin-left: -8px;
      max-width: 840px;
      padding: 4px 8px;
      font-size: $lg-font;
      font-weight: $bold-font;
      line-height: 24px;
    }
  }

  &__description {
    font-size: $sm-x-font;
    font-weight: $regular-font;
    line-height: 20px;
    color: $neutral;
    width: 100%;

    &:empty {
      display: none;
    }
  }

  &--square-avatar {
    :deep(.tm-avatar.details-hero__avatar) {
      border-radius: $border-radius-md;
      background: none !important;
      .tm-avatar-in {
        border-radius: $border-radius-md;
        .tm-icon {
          font-size: 56px !important;
        }
      }
    }
  }
}

:deep(.details-hero-auto-size-panel) {
  @include media-sm-max {
    max-width: unset;
  }
}
</style>
